import React from 'react';

import { Card, Col, Container, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import 'pages/reports/components/ReportSkeleton/ReportSkeleton.scss';

interface ReportSkeletonColumn {
    align?: 'left' | 'center';
    width?: 'wide' | 'narrow';
}

interface ReportSkeletonProps {
    cardCount?: number;
    columns: ReportSkeletonColumn[];
    hasDropdown?: boolean;
    hasViewToggles?: boolean;
    rowCount?: number;
    title: string;
}

const Bone: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`report-skeleton__bone ${className}`} />
);

export const ReportSkeleton: React.FC<ReportSkeletonProps> = ({
    cardCount = 6,
    columns,
    hasDropdown = false,
    hasViewToggles = false,
    rowCount = 8,
    title
}) => {

    const rows = Array.from({ length: rowCount });
    const cards = Array.from({ length: cardCount });

    return (
        <>
            {/* Title */}
            <Container fluid className="px-0 mb-4">
                <h5 className="fw-bold mb-3">
                    {title}
                </h5>

                {/* Summary Cards */}
                <Row className="g-3 mx-0">
                    {cards.map((_, i) => (
                        <Col key={i} lg="2" md="4" sm="4" xs="6">
                            <div className="report-skeleton__card">
                                <Bone className="report-skeleton__card-label" />
                                <Bone className="report-skeleton__card-value" />
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Table Card */}
            <Card>
                {/* Toolbar */}
                <div className="report-skeleton__toolbar">
                    <div className="d-flex align-items-end gap-3">
                        {hasViewToggles && (
                            <>
                                <div className="report-skeleton__button-group">
                                    <Bone className="report-skeleton__toggle" />
                                    <Bone className="report-skeleton__toggle" />
                                </div>
                                <div className="report-skeleton__button-group">
                                    <Bone className="report-skeleton__toggle" />
                                    <Bone className="report-skeleton__toggle" />
                                    <Bone className="report-skeleton__toggle" />
                                    <Bone className="report-skeleton__toggle" />
                                </div>
                            </>
                        )}
                        {hasDropdown && (
                            <Bone className="report-skeleton__dropdown" />
                        )}
                    </div>
                    <Bone className="report-skeleton__button" />
                </div>

                {/* Table */}
                <div className="report-skeleton__table-wrap">
                    <table className="report-skeleton__table">
                        <thead>
                            <tr>
                                {columns.map((col, i) => (
                                    <th className="report-skeleton__th" key={i}>
                                        <Bone className={`report-skeleton__th-bone report-skeleton__th-bone--${col.align === 'left' ? 'left' : col.width || 'narrow'}`} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((_, rowIdx) => (
                                <tr className="report-skeleton__tr" key={rowIdx}>
                                    {columns.map((col, colIdx) => (
                                        <td className="report-skeleton__td" key={colIdx}>
                                            <Bone
                                                className={`report-skeleton__cell-bone report-skeleton__cell-bone--${
                                                    col.align === 'left' ? (colIdx === 0 ? 'link' : 'left') : col.width || 'narrow'
                                                }`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );

};

// ===== Pre-configured skeletons for each report =====

export const Report71Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            columns={[
                { align: 'left' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'wide' }
            ]}
            title={formatMessage({ id: 'student_overview.93a3b769', defaultMessage: 'Student Overview' })}
        />
    ));

};

export const Report72Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            columns={[
                { align: 'left', width: 'wide' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'wide' }
            ]}
            title={formatMessage({ id: 'student_test_engagement.408db61f', defaultMessage: 'Student Test Engagement' })}
        />
    ));

};

export const Report73Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            hasViewToggles
            columns={[
                { align: 'left' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'narrow' }
            ]}
            title={formatMessage({ id: 'test_engagement.3947ee36', defaultMessage: 'Test Engagement' })}
        />
    ));

};

export const Report74Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            columns={[
                { align: 'left', width: 'wide' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' }
            ]}
            title={formatMessage({ id: 'question_overview.494a001d', defaultMessage: 'Question Overview' })}
        />
    ));

};

export const Report67Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            hasViewToggles
            columns={[
                { align: 'left' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'wide' }
            ]}
            cardCount={5}
            title={formatMessage({ id: 'course_summary.80626f64', defaultMessage: 'Course Summary' })}
        />
    ));

};

export const Report68Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            columns={[
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'wide' }
            ]}
            cardCount={1}
            title={formatMessage({ id: 'student_course_detail.467297bc', defaultMessage: 'Student Course Detail' })}
        />
    ));

};

export const Report69Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            hasDropdown
            columns={[
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'narrow' },
                { width: 'wide' }
            ]}
            cardCount={4}
            title={formatMessage({ id: 'resource_outcomes.4279d2e6', defaultMessage: 'Resource Outcomes' })}
        />
    ));

};

export const Report70Skeleton: React.FC = () => {

    const { formatMessage } = useIntl();

    return ((
        <ReportSkeleton
            columns={[
                { align: 'left' },
                { width: 'narrow' },
                { width: 'wide' },
                { width: 'wide' },
                { width: 'wide' }
            ]}
            cardCount={5}
            title={formatMessage({ id: 'student_summary.d4d90e98', defaultMessage: 'Student Summary' })}
        />
    ));

};